import { isAppClient } from './LocalStorage';

type GTMEventPayload = {
  event: string;
  ecommerce?: any;
  [key: string]: any;
};

function sendToNativeIfAvailable(eventData: GTMEventPayload): boolean {
  if (typeof window === 'undefined') return false;

  // ANDROID
  const androidBridge = (window as any).AndroidAnalytics;
  if (androidBridge && typeof androidBridge.trackGtmEvent === 'function') {
    try {
      androidBridge.trackGtmEvent(JSON.stringify(eventData));
      console.log('📲 Sent GTM event to Android native:', eventData.event);
      return true;
    } catch (e) {
      console.warn('⚠️ AndroidAnalytics.trackGtmEvent failed:', e);
    }
  }

  // iOS
  const iosHandlers = (window as any).webkit?.messageHandlers;
  if (iosHandlers && iosHandlers.hzcAnalytics) {
    try {
      iosHandlers.hzcAnalytics.postMessage(eventData);
      console.log('🍏 Sent GTM event to iOS native:', eventData.event);
      return true;
    } catch (e) {
      console.warn('⚠️ hzcAnalytics.postMessage failed:', e);
    }
  }

  return false;
}

export function sendGTMEvent(eventData: GTMEventPayload) {
  const GTM_ENABLED = process.env.NEXT_PUBLIC_GTM_ENABLED === 'true';

  if (!GTM_ENABLED) {
    // console.log('🚫 GTM disabled via NEXT_PUBLIC_GTM_ENABLED');
    return;
  }

  if (isAppClient()) {
    const handled = sendToNativeIfAvailable(eventData);
    if (handled) return;
  }

  if (typeof window === 'undefined') return;

  if (!('ecommerce' in eventData)) {
    console.log('⚠️ Skipped non-ecommerce event (web path):', eventData.event);
    return;
  }

  console.log('🏷️ Sending GTM event:', eventData.event, eventData);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  ((window as any).dataLayer as any[]).push(eventData);
  console.log('✅ dataLayer.push success');
}

/** CRO (SAM1-29): ecommerce olmayan dataLayer eventleri — GTM init sonrası çalışır. */
export function sendCRODataLayerEvent(payload: Record<string, unknown>) {
  const GTM_ENABLED = process.env.NEXT_PUBLIC_GTM_ENABLED === 'true';
  if (!GTM_ENABLED || typeof window === 'undefined') {
    return;
  }

  const eventData = payload as GTMEventPayload;
  if (isAppClient()) {
    const handled = sendToNativeIfAvailable(eventData);
    if (handled) {
      return;
    }
  }

  window.dataLayer = window.dataLayer || [];
  (window.dataLayer as unknown[]).push(payload);
}

/** Banner / pop-out görüntülendi (#1). */
export function sendCROPopoutView(payload: { popout_id: string; popout_name: string }) {
  sendCRODataLayerEvent({
    event: 'view_pop_out',
    ...payload,
  });
}

/** Pop-out üzerinde tıklama (#2–4). selected_button: dropdown seçimi; yoksa gönderilmez. */
export function sendCROPopoutClick(payload: {
  popout_id: string;
  popout_name: string;
  clicked_text: string;
  selected_button?: string | null;
}) {
  const body: Record<string, unknown> = {
    event: 'click_popout',
    popout_id: payload.popout_id,
    popout_name: payload.popout_name,
    clicked_text: payload.clicked_text,
  };
  if (payload.selected_button != null && String(payload.selected_button).length > 0) {
    body.selected_button = payload.selected_button;
  }
  sendCRODataLayerEvent(body);
}
